"use client";

import React from "react";

import { type BaseKey, useDeleteButton } from "@refinedev/core";
import { DeleteButtonProps } from "@refinedev/core/dist/hooks/button/delete-button";
import { Loader2, Trash } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export const DeleteButton = React.forwardRef<
  React.ComponentRef<typeof Button>,
  DeleteButtonProps
>(({ resource, id, onSuccess, accessControl, meta, ...rest }, ref) => {
  const {
    hidden,
    disabled,
    loading,
    onConfirm,
    label,
    confirmTitle: defaultConfirmTitle,
    confirmOkLabel: defaultConfirmOkLabel,
    cancelLabel: defaultCancelLabel,
  } = useDeleteButton({
    resource,
    id: id,
    accessControl,
    meta,
    onSuccess: onSuccess,
  });
  const [open, setOpen] = React.useState(false);

  const isDisabled = disabled || loading;
  const isHidden = hidden;

  if (isHidden) return null;

  const confirmCancelText = defaultCancelLabel;
  const confirmOkText = defaultConfirmOkLabel;
  const confirmTitle = defaultConfirmTitle;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <span>
          <Button
            variant="destructive"
            {...rest}
            ref={ref}
            disabled={isDisabled}
            type="button"
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <div className="flex items-center gap-2 font-semibold">
              <Trash className="h-4 w-4" />
              <span>{label}</span>
            </div>
          </Button>
        </span>
      </PopoverTrigger>
      <PopoverContent className="w-auto" align="start">
        <div className="flex flex-col gap-2">
          <p className="text-sm">{confirmTitle}</p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
              {confirmCancelText}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={loading}
              onClick={() => {
                if (typeof onConfirm === "function") {
                  onConfirm();
                }
                setOpen(false);
              }}
            >
              {confirmOkText}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
});

DeleteButton.displayName = "DeleteButton";
